import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { ScrollText, X } from "lucide-react";
import { Card } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";
import EmptyStateCard from "../../EmptyStateCard";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { saasApi } from "../shared/saasApi";
import { EventCategory, EventFilter, RiskLevel } from "../shared/saasTypes";
import { CATEGORY_LABELS, categoryLabel, formatDateTime, humanize, LOG_SOURCE_LABELS, PAGE_SIZE, RISK_ORDER } from "../shared/saasFormat";
import { ListToolbar, MetaTag, RiskBadge, ToolbarSearch } from "../shared/SaasUi";
import { WorkspaceContext } from "./workspaceContext";

const ALL = "ALL";

export default function EventsTab({ context, preset }: { context: WorkspaceContext; preset: EventFilter }) {
    const [filter, setFilter] = useState<EventFilter>(preset);
    const [searchInput, setSearchInput] = useState(preset.search ?? "");
    const search = useDebouncedValue(searchInput, 350) as string;
    const [page, setPage] = useState(1);

    useEffect(() => {
        setFilter(preset);
        setSearchInput(preset.search ?? "");
    }, [preset]);

    useEffect(() => setPage(1), [filter, search, context.connectionId, context.range]);

    const effective: EventFilter = { ...filter, search: search || undefined };
    const eventsQuery = useQuery(
        ["saas-events", context.provider, context.tenantName, context.connectionId, context.range, effective, page],
        () => saasApi.events(context.provider, context.tenantName, context.connectionId, context.range, effective, page - 1, PAGE_SIZE),
        { keepPreviousData: true },
    );
    const result = eventsQuery.data;
    const activeChips = (["actor", "resource", "action"] as const).filter((key) => filter[key]);

    return (
        <div className="space-y-2">
            <ListToolbar page={page} totalPages={result?.totalPages ?? 1} onPageChange={setPage} total={result?.totalItems ?? 0} pageSize={PAGE_SIZE}
                         label="Event pages">
                <ToolbarSearch value={searchInput} onChange={setSearchInput} placeholder="Search events, actors, IPs..." />
                <Select value={filter.category ?? ALL} onValueChange={(value) => setFilter({ ...filter, category: value === ALL ? undefined : (value as EventCategory) })}>
                    <SelectTrigger className="saas-control w-48" aria-label="Category">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All categories</SelectItem>
                        {(Object.keys(CATEGORY_LABELS) as EventCategory[]).map((category) => (
                            <SelectItem key={category} value={category}>
                                {CATEGORY_LABELS[category]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filter.minRiskLevel ?? ALL} onValueChange={(value) => setFilter({ ...filter, minRiskLevel: value === ALL ? undefined : (value as RiskLevel) })}>
                    <SelectTrigger className="saas-control w-44" aria-label="Risk">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Any risk</SelectItem>
                        {RISK_ORDER.filter((level) => level !== "INFO").map((level) => (
                            <SelectItem key={level} value={level}>
                                {humanize(level)} and above
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {activeChips.map((key) => (
                    <Button key={key} variant="outline" className="saas-outline-button" onClick={() => setFilter({ ...filter, [key]: undefined })}>
                        {humanize(key)}: {filter[key]}
                        <X className="w-3.5 h-3.5" />
                    </Button>
                ))}
            </ListToolbar>

            {eventsQuery.isLoading ? (
                <Card className="cloud-card-surface rounded-2xl h-96 animate-pulse" />
            ) : !result || result.items.length === 0 ? (
                <EmptyStateCard
                    icon={ScrollText}
                    title="No events match"
                    description="Try a wider time range or clear the filters."
                    className="cloud-card-surface border-dashed rounded-2xl"
                />
            ) : (
                <Card className="cloud-card-surface rounded-2xl p-3">
                    <div className="cloud-table-card cloud-card-surface overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-44">Time</TableHead>
                                    <TableHead>Actor</TableHead>
                                    <TableHead>Action</TableHead>
                                    <TableHead>Resource</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Risk</TableHead>
                                    <TableHead>Source</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {result.items.map((event) => (
                                    <TableRow key={event.id} className="cursor-pointer" onClick={() => context.openEvent(event.id)}>
                                        <TableCell className="text-xs whitespace-nowrap">{formatDateTime(event.occurredAt)}</TableCell>
                                        <TableCell className="text-xs">
                                            <div className="font-medium">{event.actor ?? "—"}</div>
                                            {event.ipAddress && <div className="text-muted-foreground">{event.ipAddress}{event.country ? ` · ${event.country}` : ""}</div>}
                                        </TableCell>
                                        <TableCell className="text-xs">
                                            <div className="font-mono font-medium">{event.action}</div>
                                            {event.targetName && <div className="text-muted-foreground truncate max-w-xs" title={event.targetName}>{event.targetName}</div>}
                                        </TableCell>
                                        <TableCell className="text-xs max-w-xs truncate" title={event.resourceName ?? undefined}>{event.resourceName ?? "—"}</TableCell>
                                        <TableCell className="text-xs">{event.category ? <MetaTag>{categoryLabel(event.category)}</MetaTag> : "—"}</TableCell>
                                        <TableCell className="text-xs">{event.riskLevel ? <RiskBadge level={event.riskLevel} /> : "—"}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                            {event.logSource ? LOG_SOURCE_LABELS[event.logSource] : "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            )}
        </div>
    );
}
